/**
 * Centered blocks (decisions 35, 36): a paragraph that opens with `->` and closes with an unescaped `<-`
 * (trailing spaces aside) is a `CenteredBlock`, with a `CenterMark` at each end and inline content between.
 * It may span several lines (an epigraph, a verse). A block that opens with `->` but doesn't close stays
 * a paragraph; `\<-` doesn't close. The syntax is the one part 00's fixture writes (`->text<-`).
 */
import type { BlockContext, LeafBlock, LeafBlockParser, MarkdownConfig } from '@lezer/markdown';
import { DASH, isEscaped, isSpace, LESS_THAN } from './chars.ts';

const OPEN = '->';
/** Length of each mark; the shortest centered block, `-><-`, is two of them. */
const MARK = 2;

/**
 * Where the closing `<-` starts in the content of a block that opens with `->`, or null when the block
 * doesn't close. Reading before the start gives NaN, which matches nothing, so no bounds checks are needed;
 * and since the content opens with `->`, a close can't start before index 2.
 */
export function closingMark(content: string): number | null {
  let end = content.length;
  while (isSpace(content.charCodeAt(end - 1))) end -= 1;
  const close = end - MARK;
  if (content.charCodeAt(close) !== LESS_THAN || content.charCodeAt(close + 1) !== DASH) return null;
  return isEscaped(content, close) ? null : close;
}

/** Accumulates like a paragraph and claims the block at its end if it closes. */
class CenteredParser implements LeafBlockParser {
  nextLine(): boolean {
    return false;
  }

  finish(cx: BlockContext, leaf: LeafBlock): boolean {
    const close = closingMark(leaf.content);
    if (close === null) return false;
    const from = leaf.start;
    cx.addLeafElement(
      leaf,
      cx.elt('CenteredBlock', from, from + leaf.content.length, [
        cx.elt('CenterMark', from, from + MARK),
        ...cx.parser.parseInline(leaf.content.slice(MARK, close), from + MARK),
        cx.elt('CenterMark', from + close, from + close + MARK),
      ]),
    );
    return true;
  }
}

/** The extension. */
export const centered: MarkdownConfig = {
  defineNodes: [{ name: 'CenteredBlock', block: true }, 'CenterMark'],
  parseBlock: [
    {
      name: 'CenteredBlock',
      leaf(_cx: BlockContext, leaf: LeafBlock): LeafBlockParser | null {
        return leaf.content.startsWith(OPEN) ? new CenteredParser() : null;
      },
    },
  ],
};
